import { BASE_DATE_FORMAT } from '@seeneco-ui/core/constants';
import { AxiosResponse } from 'axios';
import jscookie from 'js-cookie';
import moment from 'moment';

type ErrorType = {
	type?: string;
	message?: string;
	nativeError?: AxiosResponse | Error;
};

type TransformedErrorType = {
	originalType: string;
	type: string;
	message: string;
};

const base64: {
	encode: (str: string) => string;
	decode: (str: string) => string;
} = {
	encode: (str: string) => window.btoa(encodeURIComponent(str)),
	decode: (str: string) => decodeURIComponent(window.atob(str)),
};

const cookie = {
	get: name => jscookie.get(name),
	set: (name, value, options = {}) => jscookie.set(name, value, options),
};

function createErrorTransformer({ errorMessageMap }: { errorMessageMap: Record<string, string> }) {
	return (
		{ type, message, nativeError }: ErrorType = { type: null, message: '', nativeError: null },
	): TransformedErrorType => {
		const axiosError = nativeError as AxiosResponse;
		const stackError = nativeError as Error;
		const isStackError = nativeError instanceof Error;

		const messageMap = {
			default: nativeError
				? !isStackError
					? `Ошибка сети: ${axiosError.status}`
					: 'Ошибка javascript'
				: 'Неизвестная ошибка',
			...errorMessageMap,
		};

		if (nativeError) {
			if (!isStackError) {
				return {
					originalType: 'NETWORK_ERROR',
					type: messageMap[axiosError.status] || messageMap['default'],
					message: axiosError.statusText,
				};
			} else {
				console.error(stackError);
				return {
					originalType: 'JS',
					type: messageMap['default'],
					message: `
						NAME: ${stackError.name}
						MESSAGE: ${stackError.message}
						STACK: ${stackError.stack}
					`,
				};
			}
		}

		return {
			originalType: type,
			type: messageMap[type] || 'Неизвестная ошибка',
			message,
		};
	};
}

function declOfNum(number: number, titles: [string, string, string]) {
	// [single, few, many]
	const cases = [2, 0, 1, 1, 1, 2];
	return titles[number % 100 > 4 && number % 100 < 20 ? 2 : cases[number % 10 < 5 ? number % 10 : 5]];
}

function escapeHtmlEntities(text) {
	if (typeof text !== 'string') return text;
	var entityTable = {
		60: 'lt',
		62: 'gt',
		34: 'quot',
		38: 'amp',
		39: 'apos',
	};
	return text.replace(/[\u00A0-\u2666<>\&'"]/g, function (c) {
		return '&' + (entityTable[c.charCodeAt(0)] || '#' + c.charCodeAt(0)) + ';';
	});
}

function formatCurrency(value: number, code: string = 'RUR', locale: string = 'ru', fractions: number = 2): string {
	const getCurrencyCode = (symbolicCode: string) => {
		const currencyUnitsByCode = {
			USD: 'USD',
			EUR: 'EUR',
			RUR: 'RUB',
			JPY: 'JPY',
			GBP: 'GBP',
		};

		return currencyUnitsByCode[symbolicCode] || currencyUnitsByCode['RUR'];
	};

	return new Intl.NumberFormat(locale, {
		style: 'currency',
		currency: getCurrencyCode(code),
		minimumFractionDigits: fractions,
		maximumFractionDigits: fractions,
	}).format(value);
}

function formatDate(str: string): string {
	return moment(str, BASE_DATE_FORMAT).format('DD.MM.YYYY');
}

function formatEntity(name: string = '') {
	const pattern =
		/\"|\'|\«|\»|\s?ООО\s|\s?ИП\s|\s?ЗАО\s|\s?ПАО\s|\s?ОАО\s|\s?АО\s|\s?Акционерное общество закрытого типа\s|\s?Общество с ограниченной ответственностью\s|\s?Публичное акционерное общество\s|\s?Закрытое акционерное общество\s|\s?Открытое акционерное общество\s|\s?Акционерное общество\s|\s?Индивидуальный предприниматель\s/gim;
	const formatted = name.replace(pattern, '').trim();

	return formatted;
}

function formatLegalEntityName(name = '') {
	if (!name) return '';
	const map = {
		'Общество\\s*с\\s*ограниченной\\s*ответственностью': 'ООО',
		'Открытое\\s*акционерное\\s*общество': 'ОАО',
		'Закрытое\\s*акционерное\\s*общество': 'ЗАО',
		'Акционерное\\s*общество\\s*закрытого\\s*типа': 'АОЗТ',
		'Публичное\\s*акционерное\\s*общество': 'ПАО',
		'Акционерное\\s*общество': 'АО',
		'Индивидуальный\\s*предприниматель': 'ИП',
	};

	for (const key of Object.keys(map)) {
		const pattern = new RegExp(key, 'igm');

		if (pattern.test(name)) {
			name = name.replace(pattern, map[key]);
			break;
		}
	}

	return name;
}

const formatNumber = (number: number) => numberFormatter.format(number);

function getCookie(name: string) {
	const matches = document.cookie.match(
		new RegExp('(?:^|; )' + name.replace(/([\.$?*|{}\(\)\[\]\\\/\+^])/g, '\\$1') + '=([^;]*)'),
	);

	return matches ? decodeURIComponent(matches[1]) : undefined;
}

function getPreviousDate(currentQuarter, currentYear) {
	const isFirstQuarter = currentQuarter === 0;

	const date = isFirstQuarter
		? {
				quarter: 3,
				year: currentYear - 1,
		  }
		: {
				quarter: currentQuarter - 1,
				year: currentYear,
		  };

	return date;
}

function getPropInSafe<O, T>(obj: O, fn: (x: O) => T, replacer: any = undefined): T {
	try {
		const value = fn(obj);

		return typeof value !== 'undefined' ? value : replacer;
	} catch (err) {
		return replacer;
	}
}

function getTicketUid() {
	return getCookie('ticketuid');
}

function getURLParameterByName(name: string) {
	name = name.replace(/[\[]/, '\\[').replace(/[\]]/, '\\]');
	var regex = new RegExp('[\\?&]' + name + '=([^&#]*)'),
		results = regex.exec(location.search);
	return results == null ? '' : escapeHtmlEntities(decodeURIComponent(results[1].replace(/\+/g, ' ')));
}

function isLikeWebURI(uri: any): boolean {
	if (!uri) return false;
	if (typeof uri !== 'string') return false;
	const decodedURI = decodeURIComponent(uri);
	const pattern = /(http(s?):\/\/([\w|\d|\-|.|\/]+))|(^.?\/\S+)/g;
	const isLikeWebURI = pattern.test(decodedURI);

	return isLikeWebURI;
}

const numberFormatter = new Intl.NumberFormat('ru', { style: 'decimal' });

function setMomentLocales() {
	moment.locale('ru', {
		months: 'Январь_Февраль_Март_Апрель_Май_Июнь_Июль_Август_Сентябрь_Октябрь_Ноябрь_Декабрь'.split('_'),
		monthsShort: 'Янв_Фев_Мар_Апр_Май_Июнь_Июль_Авг_Сент_Окт_Нояб_Дек'.split('_'),
	});
}

function sortAscBy<T>(
	items: Array<T>,
	selectors: Array<{
		fn: (item: T) => any;
		isDate?: boolean;
	}>,
): Array<T> {
	const itemsImmutable = [...items];

	const compare = (a, b, selector, isDate) => {
		if (!isDate) {
			if (selector(a) === selector(b)) {
				return 0;
			}
			return selector(a) < selector(b) ? -1 : 1;
		} else {
			let format = 'DD-MM-YYYY';

			if (moment(selector(a), format).isSame(moment(selector(b), format))) {
				return 0;
			}
			return moment(selector(a), format).isBefore(moment(selector(b), format)) ? -1 : 1;
		}
	};

	const sort = (items, selectors) => {
		items.sort((a, b) => {
			for (let i = 0; i < selectors.length; i++) {
				if (compare(a, b, selectors[i].fn, selectors[i].isDate) === 0 && selectors[i + 1]) {
					return compare(a, b, selectors[i + 1].fn, selectors[i + 1].isDate);
				}
				return compare(a, b, selectors[i].fn, selectors[i].isDate);
			}
		});
	};

	if (itemsImmutable.length > 0) {
		sort(itemsImmutable, selectors);
	}

	return itemsImmutable;
}

function sortDescBy<T>(
	items: Array<T>,
	selectors: Array<{
		fn: (item: T) => any;
		isDate?: boolean;
	}>,
): Array<T> {
	const itemsImmutable = [...items];

	const compare = (a, b, selector, isDate) => {
		if (!isDate) {
			if (selector(a) === selector(b)) {
				return 0;
			}
			return selector(a) < selector(b) ? 1 : -1;
		} else {
			let format = BASE_DATE_FORMAT;

			if (moment(selector(a), format).isSame(moment(selector(b), format))) {
				return 0;
			}
			return moment(selector(a), format).isBefore(moment(selector(b), format)) ? 1 : -1;
		}
	};

	const sort = (items, selectors) => {
		items.sort((a, b) => {
			for (let i = 0; i < selectors.length; i++) {
				if (compare(a, b, selectors[i].fn, selectors[i].isDate) === 0 && selectors[i + 1]) {
					return compare(a, b, selectors[i + 1].fn, selectors[i + 1].isDate);
				}
				return compare(a, b, selectors[i].fn, selectors[i].isDate);
			}
		});
	};

	if (itemsImmutable.length > 0) {
		sort(itemsImmutable, selectors);
	}

	return itemsImmutable;
}

export {
	base64,
	cookie,
	createErrorTransformer,
	declOfNum,
	formatCurrency,
	formatDate,
	formatEntity,
	formatLegalEntityName,
	formatNumber,
	getPreviousDate,
	getPropInSafe,
	getTicketUid,
	getURLParameterByName,
	isLikeWebURI,
	setMomentLocales,
	sortAscBy,
	sortDescBy,
};
